import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCoopQuiz } from '../hooks/useCoopQuiz';
import { CoopQuizLobby } from './CoopQuizLobby';
import { CoopQuizGame } from './CoopQuizGame';
import { CoopQuizResults } from './CoopQuizResults';

interface SpotifyCoopQuizProps {
  myTracks: any[];
  herTracks: any[];
  myProfile: any;
  herProfile: any;
  onClose: () => void;
}

export const SpotifyCoopQuiz: React.FC<SpotifyCoopQuizProps> = ({
  myTracks,
  herTracks,
  myProfile,
  herProfile,
  onClose
}) => {
  const {
    session,
    sessionId,
    myPlayerNumber,
    loading,
    error,
    createSession,
    joinAsPlayer,
    setReady,
    submitAnswer,
    advanceQuestion,
    updateScores,
    deleteSession,
    getPlayer1DisplayData,
    getPlayer2DisplayData
  } = useCoopQuiz({ myTracks, herTracks, myProfile, herProfile });

  // Create session on mount
  useEffect(() => {
    console.log('🎮 [SpotifyCoopQuiz] Component mounted');
    createSession();

    // Expose delete function for testing
    (window as any).__deleteCoopSession = async () => {
      console.log('🗑️ [SpotifyCoopQuiz] Force deleting session via debug button');
      await deleteSession();
      await createSession();
    };

    // Don't delete session on unmount - let other players continue
    // Session will persist until manually ended or cleaned up by Firebase rules
    return () => {
      delete (window as any).__deleteCoopSession;
    };
  }, []);

  // Log session changes
  useEffect(() => {
    console.log('🎮 [SpotifyCoopQuiz] Session state changed:', {
      session,
      sessionId,
      myPlayerNumber,
      loading,
      error,
      status: session?.status
    });
  }, [session, sessionId, myPlayerNumber, loading, error]);

  const handleClose = async () => {
    console.log('🚪 [SpotifyCoopQuiz] Closing modal - deleting session');
    await deleteSession();
    onClose();
  };

  const handleEndSession = async () => {
    console.log('🔚 [SpotifyCoopQuiz] Ending session for all players');
    await deleteSession();
    onClose();
  };

  const handlePlayAgain = async () => {
    console.log('🔄 [SpotifyCoopQuiz] Play again - deleting session');
    await deleteSession();
    onClose();
  };

  if (loading && !session) {
    console.log('🎮 [SpotifyCoopQuiz] Rendering LOADING state');
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-purple-900/90 to-black/90 p-8 rounded-2xl max-w-2xl w-full mx-4 border border-purple-500/30"
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto mb-4"></div>
            <p className="text-white">Creando sesión co-op...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    console.log('🎮 [SpotifyCoopQuiz] Rendering ERROR state:', error);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-red-900/90 to-black/90 p-8 rounded-2xl max-w-2xl w-full mx-4 border border-red-500/30"
        >
          <div className="text-center">
            <div className="text-6xl mb-4">❌</div>
            <h3 className="text-2xl font-bold text-red-400 mb-4">Error</h3>
            <p className="text-white mb-6">{error}</p>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-red-500 text-white rounded-full font-bold hover:bg-red-600 transition-all"
            >
              Cerrar
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!session) {
    console.log('🎮 [SpotifyCoopQuiz] Rendering NULL - no session');
    return null;
  }

  console.log('🎮 [SpotifyCoopQuiz] Rendering MAIN CONTENT - session.status:', session.status, 'myPlayerNumber:', myPlayerNumber);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-gradient-to-br from-purple-900/90 to-black/90 p-8 rounded-2xl max-w-4xl w-full border border-purple-500/30 my-8"
      >
        <AnimatePresence mode="wait">
          {session.status === 'waiting' && (() => {
            console.log('🎮 [SpotifyCoopQuiz] Rendering LOBBY');
            return (
              <motion.div
                key="lobby"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <CoopQuizLobby
                  session={session}
                  myPlayerNumber={myPlayerNumber}
                  onJoinAsPlayer={joinAsPlayer}
                  onSetReady={setReady}
                  onClose={handleClose}
                  getPlayer1DisplayData={getPlayer1DisplayData}
                  getPlayer2DisplayData={getPlayer2DisplayData}
                />
              </motion.div>
            );
          })()}

          {session.status === 'playing' && myPlayerNumber && (() => {
            console.log('🎮 [SpotifyCoopQuiz] Rendering GAME');
            return (
              <motion.div
                key="game"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <CoopQuizGame
                  session={session}
                  myPlayerNumber={myPlayerNumber}
                  onSubmitAnswer={submitAnswer}
                  onAdvanceQuestion={advanceQuestion}
                  onUpdateScores={updateScores}
                  onEndSession={handleEndSession}
                  slot1Profile={myProfile}
                  slot2Profile={herProfile}
                />
              </motion.div>
            );
          })()}

          {session.status === 'finished' && myPlayerNumber && (() => {
            console.log('🎮 [SpotifyCoopQuiz] Rendering RESULTS');
            return (
              <motion.div
                key="results"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <CoopQuizResults
                  session={session}
                  myPlayerNumber={myPlayerNumber}
                  onPlayAgain={handlePlayAgain}
                  onExit={handleClose}
                  getPlayer1DisplayData={getPlayer1DisplayData}
                  getPlayer2DisplayData={getPlayer2DisplayData}
                />
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};
